/**
 * AIAssistantButton Component
 *
 * Floating action button that opens the AI assistant panel.
 * Features a gradient border matching the app's blue-to-green theme.
 */

import React from 'react';

interface AIAssistantButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

/**
 * AIAssistantButton component
 *
 * A floating round button in the bottom-right corner that opens the AI chat panel.
 * Features:
 * - Gradient border (blue to green) matching the About page hero
 * - Light/dark mode aware background
 * - Brain icon as the button face
 * - Hidden when the panel is open
 */
export const AIAssistantButton: React.FC<AIAssistantButtonProps> = ({ isOpen, onClick }) => {
  // Hide button when panel is open
  if (isOpen) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-6 right-6 z-40 group focus:outline-none"
      aria-label="Open AI Assistant"
      title="Open AI Assistant"
    >
      {/* Outer container with gradient background (creates the border effect) */}
      <div
        className="w-16 h-16 rounded-full p-[3px] shadow-lg group-hover:shadow-xl transition-all duration-200 group-hover:scale-105 group-focus:ring-2 group-focus:ring-primary-500 group-focus:ring-offset-2 dark:group-focus:ring-offset-gray-900"
        style={{
          background: 'linear-gradient(135deg, #004581 0%, #03A832 100%)',
        }}
      >
        {/* Inner container with solid background */}
        <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden">
          {/* Light mode image */}
          <img
            src="/assets/medsuite-ai-sillouette.png"
            alt="AI Assistant"
            className="w-[130%] h-[130%] object-cover dark:hidden"
          />
          {/* Dark mode image */}
          <img
            src="/assets/medsuite-ai-sillouette-darkmode.png"
            alt="AI Assistant"
            className="w-[130%] h-[130%] object-cover hidden dark:block"
          />
        </div>
      </div>
    </button>
  );
};

export default AIAssistantButton;
